import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { FileText, FolderOpen } from 'lucide-react';
import { useUploadProducts } from '../../features/products/hooks';
import { downloadTemplate, downloadTemplateExcel } from '../../features/products/api';
import type { BulkUploadResult, ImageSaveDestination } from '../../features/products/types';
import type { AsyncJob } from '../../services/asyncJobApi';
import { useToast } from '../../hooks/useToast';
import { getErrorMessage } from '../../utils/getErrorMessage';
import { Button } from '../../components/common/Button';
import { BackButton } from '../../components/common/BackButton';
import { RecommendedBadge } from '../../components/common/RecommendedBadge';
import { brandConfig } from '../../config/brands';
import './ManualProductUploadPage.css';

const ACCEPTED_FORMATS = '.csv,.xlsx,.xls';

export default function ManualProductUploadPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<{ headers: string[]; rows: string[][] } | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [result, setResult] = useState<BulkUploadResult | null>(null);
  const [imageSaveDestination, setImageSaveDestination] = useState<ImageSaveDestination>(() => {
    const saved = localStorage.getItem('upload_imageSaveDestination');
    return saved === 'cloud' || saved === 'none' ? saved : 'temporary';
  });

  const handleDestinationChange = (val: ImageSaveDestination) => {
    setImageSaveDestination(val);
    localStorage.setItem('upload_imageSaveDestination', val);
  };

  const handleProgress = useCallback((job: AsyncJob) => {
    setProgress(job.progress ?? 0);
  }, []);

  const uploadProducts = useUploadProducts({ onProgress: handleProgress });

  const parseFilePreview = async (file: File) => {
    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json<string[]>(ws, { header: 1 });

      if (data.length < 2) {
        setPreviewData(null);
        return;
      }
      const headers = data[0].map(String);
      const rows = data.slice(1, 4).map(row =>
        headers.map((_, i) => (row[i] != null ? String(row[i]) : ''))
      );
      setPreviewData({ headers, rows });
    } catch {
      setPreviewData(null);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setResult(null);
      setProgress(0);
      parseFilePreview(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      const ext = file.name.toLowerCase();
      if (ext.endsWith('.csv') || ext.endsWith('.xlsx') || ext.endsWith('.xls')) {
        setSelectedFile(file);
        setResult(null);
        setProgress(0);
        parseFilePreview(file);
      } else {
        toast.error('CSV または Excel (.xlsx/.xls) ファイルを選択してください');
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setProgress(0);

    try {
      const res = await uploadProducts.mutateAsync({ file: selectedFile, imageSaveDestination });
      setResult(res);
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
      if (res.success_count > 0 && res.error_count === 0) {
        toast.success(`${res.success_count}件の${brandConfig.labels.productShort}を登録しました`);
      } else if (res.success_count > 0) {
        toast.success(`${res.success_count}件登録、${res.error_count}件エラー`);
      } else {
        toast.error('登録に失敗しました');
      }
    } catch (err: any) {
      toast.error(`アップロードに失敗しました: ${getErrorMessage(err)}`);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadTemplate();
    } catch {
      toast.error('テンプレートのダウンロードに失敗しました');
    }
  };

  const handleDownloadTemplateExcel = async () => {
    try {
      await downloadTemplateExcel();
    } catch {
      toast.error('テンプレートのダウンロードに失敗しました');
    }
  };

  return (
    <div className="manual-product-upload-page">
      <BackButton to="/products/manual" />
      <h1 className="upload-page-title">Excel・CSVから{brandConfig.labels.productShort}を一括登録</h1>

      <div className="upload-main-content">
        <div className="upload-left-column">
          <div className="upload-instructions">
            <h2>アップロード手順</h2>
            <ol>
              <li>
                テンプレートファイルをダウンロード
                <div className="template-buttons">
                  <Button variant="muted" onClick={handleDownloadTemplateExcel}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <img src="/microsoft_excel_logo.svg" alt="" width="24" height="24" style={{ borderRadius: '3px' }} />
                      Excelテンプレート
                      <RecommendedBadge marginRight={0} />
                    </span>
                  </Button>
                  <Button variant="muted" onClick={handleDownloadTemplate}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <img src="/file_csv_2.svg" alt="" width="24" height="24" />
                      CSVテンプレート
                    </span>
                  </Button>
                </div>
              </li>
              <li>
                ファイルに{brandConfig.labels.productShort}情報を入力
                <p style={{ margin: '4px 0 4px', fontSize: '0.78em', color: 'rgba(245, 241, 230, 0.4)' }}>
                  ※「title」（{brandConfig.labels.productName}）は必須です。その他はオプションです。
                </p>
                <p style={{ margin: '0 0 0', fontSize: '0.78em', color: 'rgba(245, 241, 230, 0.4)' }}>
                  ※{brandConfig.labels.productShort}画像はURLで指定することもできますが、ファイルアップロード後に手動で画像を追加することもできます。
                </p>
              </li>
              <li>右のエリアからファイルをアップロード</li>
            </ol>
          </div>
        </div>

        <div className="upload-right-column">
          <div
            className={`upload-dropzone ${selectedFile ? 'has-file' : ''}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_FORMATS}
              onChange={handleFileSelect}
              hidden
            />
            {selectedFile ? (
              <div className="selected-file">
                <span className="file-icon">
                  <FileText size={18} aria-hidden="true" />
                </span>
                <span className="file-name">{selectedFile.name}</span>
                <span className="file-size">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
              </div>
            ) : (
              <div className="dropzone-placeholder">
                <span className="dropzone-icon">
                  <FolderOpen size={32} aria-hidden="true" />
                </span>
                <p>ファイルをドラッグ＆ドロップ、またはクリックして選択</p>
                <p className="dropzone-hint">Excel (.xlsx, .xls), CSV に対応</p>
              </div>
            )}
          </div>

          {!previewData && (
            <div className="upload-actions">
              <p className="upload-actions-hint">ファイルを選択するとプレビューが表示されます</p>
            </div>
          )}
        </div>
      </div>

      {previewData && (
        <>
          <h2 className="upload-preview-title">プレビュー（先頭{previewData.rows.length}件）</h2>
          <div className="preview-table-wrapper">
            <table className="preview-table">
              <thead>
                <tr>{previewData.headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {previewData.rows.map((row, i) => (
                  <tr key={i}>{row.map((cell, j) => <td key={j} title={cell || undefined}>{cell || '—'}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="upload-register-actions">
            <div className="image-save-destination">
              <label className="gen-label">
                <span>{brandConfig.labels.productShort}画像を素材に自動追加</span>
                <select
                  value={imageSaveDestination}
                  onChange={(e) => handleDestinationChange(e.target.value as ImageSaveDestination)}
                  disabled={uploadProducts.isPending}
                  className={imageSaveDestination !== 'none' ? 'has-value' : ''}
                >
                  <option value="cloud">共有フォルダに自動追加</option>
                  <option value="temporary">個人フォルダに自動追加</option>
                  <option value="none">自動追加しない</option>
                </select>
              </label>
            </div>
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={!selectedFile || uploadProducts.isPending}
            >
              {uploadProducts.isPending ? '処理中...' : `${brandConfig.labels.productShort}を登録`}
            </Button>
          </div>
        </>
      )}

      {uploadProducts.isPending && (
        <div className="upload-progress">
          <p className="upload-progress-label">
            {brandConfig.labels.productShort}データを登録中... {progress}%
          </p>
          <div className="upload-progress-bar">
            <div
              className="upload-progress-bar-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="upload-progress-hint">
            この画面を閉じずにお待ちください
          </p>
        </div>
      )}

      {result && (
        <div className="upload-result" ref={resultRef}>
          <h2>登録結果</h2>
          <div className="result-summary">
            <span className="result-success">成功: {result.success_count}件</span>
            {result.error_count > 0 && (
              <span className="result-error">エラー: {result.error_count}件</span>
            )}
          </div>

          {(result.image_save_count != null && result.image_save_count > 0) && (
            <div className="result-summary" style={{ marginTop: '8px' }}>
              <span className="result-success">画像保存: {result.image_save_count}枚</span>
              {(result.image_save_error_count ?? 0) > 0 && (
                <span className="result-error">画像保存エラー: {result.image_save_error_count}枚</span>
              )}
            </div>
          )}

          {result.errors.length > 0 && (
            <div className="result-errors">
              <h3>エラー詳細</h3>
              <table className="error-table">
                <thead>
                  <tr>
                    <th>行</th>
                    <th>エラー内容</th>
                  </tr>
                </thead>
                <tbody>
                  {result.errors.map((err, i) => (
                    <tr key={i}>
                      <td>{err.row}</td>
                      <td>{err.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {result.success_count > 0 && (
            <Button variant="muted" onClick={() => navigate('/products/manual')}>
              {brandConfig.labels.productShort}一覧へ
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
